import Button from '@ui/Button';
import Input from '@ui/Input';
import React from 'react';
import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Typography from '@ui/Typography';
import Select from '@ui/Select';
import { getAllISOCodes } from 'iso-country-currency';
import { defaults, pick, remove, sortBy } from 'lodash-es';

const schema = yup.object().shape({
  country: yup
    .object()
    .required('Please select a country')
    .typeError('Please select a country'),
  zipcode: yup
    .string()
    .required('Zipcode is required')
    .min(5, 'Zipcode must be at least 5 characters')
    .max(10, 'Zipcode must be at most 10 characters'),
  linkedInProfile: yup.string().notRequired().nullable(),
});

const getCountries = () => {
  const countries = sortBy(
    getAllISOCodes().map((country) => ({
      label: country.countryName,
      id: country.iso,
    })),
    'label'
  );

  const priorityCountries = remove(countries, (country) =>
    ['US', 'IN', 'MX'].includes(country.id)
  );

  return [
    priorityCountries.find((c) => c.id === 'US'),
    priorityCountries.find((c) => c.id === 'IN'),
    priorityCountries.find((c) => c.id === 'MX'),
    ...countries,
  ];
};

function NonProfitOnBoardFormLocationStep(props) {
  const { onSubmit, onBack, formData } = props;

  const defaultValues = defaults(
    pick(formData, ['country', 'zipcode', 'linkedInProfile']),
    {
      linkedInProfile: '',
      zipcode: '',
      country: null,
    }
  );

  const form = useForm({
    defaultValues: defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = form;

  const countries = getCountries();

  const handleFormSubmit = (data) => {
    onSubmit(data);
  };

  return (
    <div className="flex flex-col gap-12 flex-1 w-full">
      <Typography variant="dialogtitle">Set Up Your Account</Typography>

      <form
        onSubmit={handleSubmit(handleFormSubmit)}
        className="flex flex-col gap-6">
        <Controller
          name="country"
          control={control}
          render={({ field: { ref, ...rest } }) => (
            <Select
              label="Country"
              placeholder="Country of residence"
              required
              options={countries}
              {...rest}
              error={errors.country?.message}
            />
          )}
        />

        <Controller
          name="zipcode"
          control={control}
          render={({ field: { ref, ...rest } }) => (
            <Input
              label="Zipcode"
              required
              {...rest}
              error={errors.zipcode?.message}
            />
          )}
        />

        <Controller
          name="linkedInProfile"
          control={control}
          render={({ field: { ref, ...rest } }) => (
            <Input label="LinkedIn Profile " {...rest} />
          )}
        />

        <div className="flex justify-between flex-rows gap-4">
          <div className="flex-flow-col">
            <Button
              variant="outlined"
              color="primary"
              className="mt-2"
              onClick={onBack}>
              Back
            </Button>
          </div>

          <div className="flex-flow-col">
            <Button
              variant="filled"
              color="primary"
              type="submit"
              className="mt-2">
              Next
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default NonProfitOnBoardFormLocationStep;
